import React, { useEffect, useCallback, useMemo, memo, MouseEvent } from 'react'
import { SwiperProps, Swiper, SwiperSlide, SwiperClass } from 'swiper/react'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { RNPostMessage, AFWebLinkType } from '../js/postMessage'
import { Link, NavigateFunction, useNavigate } from 'react-router-dom'
import { onErrorImg } from '../js/script'
import { BannerAccount } from '../@types/home'
import { useAppContext } from '../components/provider/AppProvider'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

interface MainCarouselProps {
  items: BannerAccount[]
  onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, item?: BannerAccount) => void
}

const MainCarousel: React.FC<MainCarouselProps> = ({ items, onClick }) => {
  const swiper = React.useRef<SwiperClass | null>(null)
  const { isAppView } = useAppContext()
  const navigate: NavigateFunction = useNavigate()

  const filteredItems = useMemo(
    () => items.filter((item) => item?.banners[0]?.landingUrl && item?.banners[0]?.imageUrl),
    [items]
  )

  // const modifiedItems: BannerAccount[] = useMemo(
  //   () => [filteredItems[filteredItems.length - 1], ...filteredItems.slice(0, filteredItems.length - 1)],
  //   [filteredItems]
  // )

  const modifiedItems: BannerAccount[] = useMemo(
    () =>
      filteredItems.length > 1
        ? [filteredItems[filteredItems.length - 1], ...filteredItems.slice(0, filteredItems.length - 1)]
        : filteredItems,
    [filteredItems]
  )

  const setSwiper = useCallback((newSwiper: SwiperClass) => {
    swiper.current = newSwiper
  }, [])

  const renderPagination = useCallback((swiper: SwiperClass) => {
    if (!swiper || !swiper.slides || swiper.slides.length === 0) return ''
    const total = swiper.slides.length // 실제 슬라이드 수
    const current = typeof swiper.realIndex === 'number' && !isNaN(swiper.realIndex) ? swiper.realIndex : 1 // 현재 슬라이드 인덱스

    // realIndex를 modifiedItems의 인덱스로 조정
    const adjustedCurrent = (current === 0 ? total - 1 : current - 1) + 1

    return `<span class='swiper-pagination-current'>${adjustedCurrent}</span><span class='swiper-pagination-bar'> / </span><span class='swiper-pagination-total'>${total}</span>`
  }, [])

  const swiperModules = useMemo(() => [Autoplay, Pagination, Navigation], [])
  const swiperBreakpoints = useMemo(
    () => ({
      768: {
        spaceBetween: 32,
        centeredSlides: true,
      },
    }),
    []
  )

  const swiperProps: SwiperProps = useMemo(
    () => ({
      onSwiper: setSwiper,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
        waitForTransition: false,
      },
      pagination: {
        type: 'custom',
        renderCustom: renderPagination,
      },
      modules: swiperModules,
      className: 'home_banner_slide',
      spaceBetween: 0,
      slidesPerView: 'auto',
      loop: true,
      initialSlide: 0,
      breakpoints: swiperBreakpoints,
      observer: true,
      observeParents: true,
    }),
    [setSwiper, renderPagination, swiperModules, swiperBreakpoints]
  )

  const onLinkHandler = (event: MouseEvent<HTMLAnchorElement>, item: BannerAccount) => {
    event.preventDefault()

    const { banners } = item
    if (isAppView) {
      RNPostMessage(AFWebLinkType.moveToLink, {
        url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}${banners[0].landingUrl}`,
      })
    } else {
      navigate(banners[0].landingUrl)
    }
  }

  useEffect(() => {
    setTimeout(() => {
      if (!swiper.current) return
      swiper.current.update()
      swiper.current.autoplay.start()
    }, 100)
  }, [items])

  useEffect(() => {
    // 첫 번째 슬라이드로 이동
    if (swiper.current && modifiedItems.length > 0) swiper.current.slideToLoop(1, 0)
  }, [modifiedItems])

  return (
    <div className='home_banner'>
      {modifiedItems.length > 0 && (
        <Swiper {...swiperProps}>
          {modifiedItems.map((item: BannerAccount, idx: number) => (
            <SwiperSlide key={`${item?.accountNo}-${idx}`}>
              <Link
                to={item?.banners[0]?.landingUrl}
                onClick={(e: MouseEvent<HTMLAnchorElement>) => onLinkHandler(e, item)}
              >
                <img src={item?.banners[0]?.imageUrl} alt='' onError={onErrorImg} />
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  )
}

export default memo(MainCarousel)
